import { Alert, Box, Button, Typography } from '@mui/material'
import { ChevronRight, ChevronLeft } from '@mui/icons-material'
import { useState, useContext, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import FileUploadService from '../../../../../shared/services/file-upload/file-upload.service'
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications'
import { DataTypeSelectionStep } from './steps/data-type-selection-step'
import { UploadMethodStep } from './steps/upload-method-step'
import { ValidationResultsStep } from './steps/validation-results-step'
import { LinkReconciliationStep } from './steps/link-reconciliation-step'
import { ImportSuccessStep } from './steps/import-success-step'
import { SheetConfigurationStep } from './steps/sheet-configuration-step'
import { CustomMappingStep } from './steps/custom-mapping-step'
import { ImportStepHeader } from './steps/import-step-header'
import { FileUploadProgress } from './steps/file-upload-progress'
import { FileCommitProgress } from './steps/file-commit-progress'
import { LoadingSpinner } from '../../../../presentation-components/display-elements-components/loading-spinner'
import { getImportFileType } from '../../../../../shared/utils/import-utils'
import { BillType, IFileUpload } from '../../../../../shared/services/file-upload/file-upload.model'

// Import types whose commit runs through the worker queue and reports through the upload's
// lineCommitStatus. Mirrors createRecords() on the backend: these five are marked QUEUED and
// committed asynchronously, so the wizard watches commit progress for them. Every other type
// (suppliers, locations, vehicles, projects, products) is committed synchronously inside the
// import request itself and lineCommitStatus never leaves NOT_REQUESTED, so there is no run
// to watch: the response IS the result.
const ASYNC_COMMIT_TYPES = new Set([
  'emissions-rules',
  'emissions-manual',
  'emissions-direct',
  'emissions-product',
  'spend-based',
])
const isAsyncCommitType = (importType: string | null | undefined): boolean =>
  !!importType && ASYNC_COMMIT_TYPES.has(importType)

// Parse a ?step value defensively: NaN falls back to 0, and the index is clamped to the
// longest flow's final step (4). It is not clamped to the CURRENT steps.length because on a
// fresh mount the flow shape (4 or 5 steps) is not known until the upload is fetched, and a
// linked-flow ?step=4 must not be pulled down to 3 in the meantime; both the renderer and
// the header tolerate 4 in a four-step flow.
const parseStepParam = (value: string | null): number => {
  const parsed = value ? parseInt(value) : 0
  if (Number.isNaN(parsed)) {
    return 0
  }
  return Math.max(0, Math.min(parsed, 4))
}

export const ImportDataWizard = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const supplierIdFromUrl = searchParams.get('supplierId') || ''
  const startFromUrl = (searchParams.get('start') || '').toLowerCase()
  const dataTypeFromUrl = (searchParams.get('dataType') || '').toLowerCase()
  const fileUploadIdFromUrl = searchParams.get('fileUploadId') || ''
  const stepFromUrl = searchParams.get('step')
  //Services
  const fileUploadServiceRef = useRef(new FileUploadService())
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [activeStep, setActiveStep] = useState(parseStepParam(stepFromUrl))
  const [selectedDataType, setSelectedDataType] = useState<string>()
  const [fileData, setFileData] = useState<any>([])
  const [isUploading, setIsUploading] = useState(false)
  const [emissionType, setEmissionType] = useState<string>('activity')
  const [selectedReportPeriodId, setSelectedReportPeriodId] = useState<string>('')
  const [uploadMode, setUploadMode] = useState<'template' | 'custom' | 'bills' | null>('template')
  const [billType, setBillType] = useState<BillType>('energy')
  // One decision, held once: whether rows are categorised by rules or picked manually. It
  // parameterises the template download AND how the uploaded file is read (they were two separate
  // states before, which rendered the same radio group twice with different meanings).
  const [templateType, setTemplateType] = useState<'rules' | 'manual'>('rules')
  const [linkToExistingEmission, setLinkToExistingEmission] = useState<boolean>(false)
  const [uploadResult, setUploadResult] = useState<any>(null)
  const [importResult, setImportResult] = useState<any>(null)
  // The wizard stays mounted through the whole flow, so the two long-running phases are
  // plain step content driven by these flags: file processing renders inside step 2 until
  // processingDone, and (for async commit types) the commit renders inside the final step
  // until commitDone. Sync types set commitDone immediately off the import response.
  const [processingDone, setProcessingDone] = useState<boolean>(false)
  const [commitDone, setCommitDone] = useState<boolean>(false)
  // A commit has been asked for on this upload (this session, or an active run found on
  // restore). From that point the earlier steps are off limits: history traversal
  // fast-forwards to the final step, and the Import action stays disabled, so the same
  // upload cannot be committed a second time mid-run.
  const [commitRequested, setCommitRequested] = useState<boolean>(false)
  // Restore-from-URL failed (refresh with an upload the API could not return): shown in
  // place of the step content that would have needed it, with a retry.
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [restoreAttempt, setRestoreAttempt] = useState<number>(0)
  const [typeOfData, setTypeOfData] = useState<string | null>(null)
  const [customSheetNumber, setCustomSheetNumber] = useState<number>(1)
  const [customHeaderRow, setCustomHeaderRow] = useState<number>(1)
  const [customMappingComplete, setCustomMappingComplete] = useState<boolean>(false)
  const [canImport, setCanImport] = useState<boolean>(false)
  const [canImportFromReconciliation, setCanImportFromReconciliation] = useState<boolean>(false)
  const [hasFetchedPendingErrors, setHasFetchedPendingErrors] = useState<boolean>(false)
  const [failedCount, setFailedCount] = useState<number>(0)
  const [validCount, setValidCount] = useState<number>(0)
  const [showReportPeriodError, setShowReportPeriodError] = useState<boolean>(false)
  const [importMode, setImportMode] = useState<'create' | 'update'>('create')
  const [importMatchKey, setImportMatchKey] = useState<'name' | 'contactSupplierReference'>('name')
  // Server-wide feature flag: bill (PDF/image) extraction. When off, the "Upload
  // bills" option is hidden rather than offered and failing with a 503.
  const [billExtractionEnabled, setBillExtractionEnabled] = useState<boolean>(false)
  // Factor source/year selection lives here (not in the upload step) because it now does double
  // duty: it parameterises the template download AND, when pinFactorYear is ticked, it is sent with
  // the upload so every row is calculated against that factor year instead of its invoice date.
  const [selectedConversionSourceId, setSelectedConversionSourceId] = useState<string>('')
  const [selectedConversionYear, setSelectedConversionYear] = useState<string>('')
  const [pinFactorYear, setPinFactorYear] = useState<boolean>(false)
  //Variables
  const customSpreadsheetSupported = new Set([
    'emissions:activity',
    'emissions:spend',
    'emissions:direct',
    'emissions:products',
    'projects',
    'suppliers',
    'locations',
    'vehicles',
    'products',
  ])
  const isCustomAllowed = !!(selectedDataType && (
    customSpreadsheetSupported.has(`${selectedDataType}:${emissionType}`) ||
    customSpreadsheetSupported.has(selectedDataType)
  ))
  const isCustomSpreadsheet = isCustomAllowed && uploadMode === 'custom'
  // Custom spreadsheets are always read with rules (there is no manual custom template), so the
  // merged rules/manual choice is forced there; everywhere else the user's choice stands.
  const effectiveTemplateType: 'rules' | 'manual' = uploadMode === 'custom' ? 'rules' : templateType
  // Bills are only an activity-emissions concept: a utility/fuel bill is activity data by
  // definition, and the extracted rows converge on the emissions-rules template. Also gated on
  // the server-wide bill-extraction flag so a disabled environment does not offer the option.
  const isBillAllowed = selectedDataType === 'emissions' && emissionType === 'activity' && billExtractionEnabled
  const isBillUpload = isBillAllowed && uploadMode === 'bills'
  // Note: the review step does NOT read isBillUpload. ValidationResultsStep keys off the
  // fetched upload's own sourceType instead, which also survives a mid-flow refresh.
  const isBillReview = uploadResult?.sourceType === 'BILL_DOCUMENTS'
  const hasReconciliationStep = (uploadResult?.linkToExistingEmission === true || linkToExistingEmission)
    && selectedDataType === 'emissions'
    && emissionType === 'activity'
  const successStepIndex = hasReconciliationStep ? 4 : 3
  const steps = [
    'Select data type',
    'Upload',
    isCustomSpreadsheet ? 'Set up & map columns' : 'Review',
    ...(hasReconciliationStep ? ['Match links'] : []),
    'Import complete'
  ]
  // The upload the flow is currently working on: from memory while mounted, from the URL
  // after a refresh (the fetch effect below then restores what the API can tell us).
  const activeUploadId: string = uploadResult?.id || fileUploadIdFromUrl || ''
  // Step 2 is showing the processing run rather than its real content (review or mapping).
  // Used to swap the footer's Next button out while the run is in flight.
  const isAwaitingProcessing = activeStep === 2 && !processingDone && !!activeUploadId
    && (!isCustomSpreadsheet || (!!uploadResult && customMappingComplete))

  // The URL mirrors where the user is (?step, ?fileUploadId, ?dataType) purely as a
  // bookmark/refresh aid: in-memory state is the source of truth while the wizard is
  // mounted, and a refresh falls back to what the API can recover. Steps are pushed (not
  // replaced) so the browser's Back button walks back through the wizard.
  const changeStep = (step: number, options?: { replace?: boolean; fileUploadId?: string; dataType?: string }) => {
    setActiveStep(step)
    setSearchParams(previous => {
      const next = new URLSearchParams(previous)
      next.set('step', String(step))
      if (options?.fileUploadId) {
        next.set('fileUploadId', options.fileUploadId)
      }
      if (options?.dataType) {
        next.set('dataType', options.dataType)
      }
      return next
    }, { replace: options?.replace })
  }

  // Drops a stale ?fileUploadId (called when a fresh upload is about to replace it, or the
  // mapping step abandons one); without this the restore-from-URL fetch would immediately
  // resurrect the abandoned upload.
  const clearUrlFileUploadId = () => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous)
      next.delete('fileUploadId')
      return next
    }, { replace: true })
  }

  useEffect(() => {
    fileUploadServiceRef.current.getBillExtractionStatus()
      .then(({ enabled }) => setBillExtractionEnabled(!!enabled))
      .catch(() => setBillExtractionEnabled(false))
  }, [])

  useEffect(() => {
    const shouldJumpToUpload = !!supplierIdFromUrl || startFromUrl === 'upload'
    const nextType = dataTypeFromUrl || (shouldJumpToUpload ? 'emissions' : '')
    if (!selectedDataType && nextType) {
      setSelectedDataType(nextType)
    }
    if (shouldJumpToUpload && activeStep === 0) {
      changeStep(1, { replace: true, dataType: nextType || undefined })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // React to ?step after mount too, so browser Back/Forward moves between steps instead of
  // stranding the page (in-page history traversal never remounts this component).
  // Wizard-initiated changes go through changeStep, which writes the same value it sets in
  // state, so this only takes effect for history traversal.
  useEffect(() => {
    const parsedStep = parseStepParam(searchParams.get('step'))
    // Once a commit has been requested the earlier steps no longer exist: the rows are
    // (being) committed, and re-showing review would offer a second import of the same
    // upload. History traversal that lands below the final step fast-forwards instead.
    if (commitRequested && parsedStep < successStepIndex) {
      changeStep(successStepIndex, { replace: true })
      return
    }
    if (parsedStep !== activeStep) {
      setActiveStep(parsedStep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    // If fileUploadId is in the URL and nothing is held in memory (a fresh mount: refresh
    // mid-flow, an old link, a redirect from the standalone progress pages), fetch the
    // upload and restore what the API can tell us. Mid-flow this stays quiet, because every
    // upload path (bills included) puts at least a stub uploadResult in memory: the flow's
    // own state is the source of truth while the wizard is mounted.
    if (fileUploadIdFromUrl && !uploadResult) {
      let cancelled = false
      fileUploadServiceRef.current.getUpload(fileUploadIdFromUrl)
        .then((response) => {
          // The id may have been superseded (a new upload started, the param cleared)
          // while this fetch was in flight; applying it anyway would restore the wrong
          // upload over live state.
          if (cancelled) {
            return
          }
          setRestoreError(null)
          setUploadResult(response)
          setLinkToExistingEmission(response.linkToExistingEmission === true)
          // Determine typeOfData from the upload result details
          if (response.details) {
            setTypeOfData(response.details)
            // Set selectedDataType if not already set
            if (!selectedDataType) {
              if (response.details.includes('emissions') || response.details === 'spend-based') {
                setSelectedDataType('emissions')
              } else {
                setSelectedDataType(response.details)
              }
            }
            // details encodes the emission type; restore it unconditionally, not only when
            // selectedDataType was empty: the mount effect sets selectedDataType from
            // ?dataType before this fetch resolves, and skipping the restore then leaves a
            // stale emission-type radio and a wrong importType on a re-import.
            const emissionTypeByDetails: { [details: string]: string } = {
              'spend-based': 'spend',
              'emissions-direct': 'direct',
              'emissions-product': 'products',
              'emissions-rules': 'activity',
              'emissions-manual': 'activity',
            }
            if (emissionTypeByDetails[response.details]) {
              setEmissionType(emissionTypeByDetails[response.details])
            }
          }
          // Pick the phase the upload is actually in, so a refresh lands sensibly: still
          // processing shows the progress run, completed falls through to review.
          setProcessingDone(response.lineImportStatus === 'COMPLETED')
          // The endpoint spells the flag with a lowercase s; the model with a capital one.
          // Accept either rather than betting on which shape this response has.
          const isCustomUpload = (response as any).isCustomSpreadsheet === true || response.isCustomSpreadSheet === true
          if (isCustomUpload && response.mapped !== true) {
            // A custom upload whose columns were never mapped: reopen the mapping step
            // rather than polling a processing run that will never start.
            setUploadMode('custom')
            setCustomMappingComplete(false)
          }
          if (!isAsyncCommitType(response.details)) {
            // A synchronous commit type: there is never a commit run to watch, so the
            // final step (reachable here only if the import already ran) shows the
            // success summary directly.
            setCommitDone(true)
          }
          if (response.lineCommitStatus === 'PENDING' || response.lineCommitStatus === 'IN_PROGRESS') {
            // A commit is already running server-side: resume watching it (and lock the
            // earlier steps, exactly as if this session had requested it). The final step
            // hosts the commit progress until the backend reports done.
            setCommitRequested(true)
            changeStep(response.linkToExistingEmission === true ? 4 : 3, { replace: true })
          }
        })
        .catch((err) => {
          if (cancelled) {
            return
          }
          console.error('Error fetching upload result:', err)
          setRestoreError(err?.response?.data?.message
            || 'The upload could not be loaded. Check your connection and try again.')
        })
      return () => { cancelled = true }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadIdFromUrl, uploadResult, restoreAttempt, emissionType, selectedDataType, userNotificationsContext])

  const handleEmissionTypeChange = (event: any) => {
    setEmissionType(event.target.value)
    if (event.target.value !== 'activity') {
      setLinkToExistingEmission(false)
    }
    setShowReportPeriodError(false)
    setSelectedConversionSourceId('')
    setSelectedConversionYear('')
    setPinFactorYear(false)
  }

  const handleReportPeriodChange = (event: any) => {
    setSelectedReportPeriodId(event.target.value)
    if (event.target.value) {
      setShowReportPeriodError(false)
    }
  }

  const isSpendPin = selectedDataType === 'emissions' && emissionType === 'spend' && pinFactorYear

  // A ticked pin with no source or year would silently upload unpinned, so it blocks the upload
  // with a message instead.
  const blockOnIncompletePin = () => {
    if (isSpendPin && (!selectedConversionSourceId || !selectedConversionYear)) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: 'Choose a factor source and year for this upload, or untick the single factor year option.',
        backgroundColor: 'text.primary'
      })
      return true
    }
    return false
  }

  const appendPinnedFactorYear = (form: FormData) => {
    if (isSpendPin && selectedConversionSourceId && selectedConversionYear) {
      form.append('pinnedConversionSourceId', selectedConversionSourceId)
      form.append('pinnedConversionYear', selectedConversionYear)
    }
  }

  const handleUploadModeSelect = (mode: 'template' | 'custom' | 'bills') => {
    setUploadMode(mode)
    // Switching mode changes what the dropzone accepts, so a file staged under the previous mode is
    // no longer valid (a CSV cannot be a bill, and vice versa).
    setFileData([])
  }

  const handleTemplateTypeChange = (type: 'rules' | 'manual') => {
    setTemplateType(type)
  }

  // One honest confirm for every partial import: it says how many rows will import and how many
  // will be skipped, instead of a vague "some records are not valid". Resolves true when the user
  // chooses to import the valid rows.
  const confirmPartialImport = async (): Promise<boolean> => {
    const failedSentence = failedCount === 1
      ? '1 row has errors and will not be imported.'
      : `${failedCount} rows have errors and will not be imported.`
    // Every row failed: there is nothing to import, so acknowledge rather than offer "Import 0 rows"
    // (committing zero rows would only flip the upload's status).
    if (validCount === 0) {
      await userNotificationsContext.openDialog({
        active: true,
        title: 'Nothing to import',
        message: `${failedSentence} Fix the rows here or later in Import History, then import again.`,
        buttonCopy: 'OK',
        hideCancelButton: true
      })
      return false
    }
    // Linked-activity imports resolve each row's link at commit time and skip rows that cannot be
    // resolved, so no exact import count is promised on this path (the success step hedges the
    // same way).
    if (hasReconciliationStep) {
      const response = await userNotificationsContext.openDialog({
        active: true,
        title: 'Some rows have errors',
        message: `${failedSentence} Rows that link cleanly will be imported; you can check the outcome in Import History.`,
        buttonCopy: 'Import',
        cancelButtonCopy: 'Cancel',
        hideCancelButton: false
      })
      return response === true
    }
    const validLabel = `${validCount} ${validCount === 1 ? 'row' : 'rows'}`
    // Bills: the rows left behind are the review's unfinished jobs, not errors in a file.
    if (isBillReview) {
      const billResponse = await userNotificationsContext.openDialog({
        active: true,
        title: `Import ${validLabel}?`,
        message: `${failedCount === 1 ? '1 row is' : `${failedCount} rows are`} still on your list of things to do and will not be imported now. You can finish ${failedCount === 1 ? 'it' : 'them'} later in Import History.`,
        buttonCopy: `Import ${validLabel}`,
        cancelButtonCopy: 'Cancel',
        hideCancelButton: false
      })
      return billResponse === true
    }
    const response = await userNotificationsContext.openDialog({
      active: true,
      title: 'Import valid rows only?',
      message: `Import ${validLabel} and skip the ${failedCount === 1 ? 'one' : failedCount} with errors? ${failedSentence} You can fix ${failedCount === 1 ? 'it' : 'them'} later in Import History.`,
      buttonCopy: `Import ${validLabel}`,
      cancelButtonCopy: 'Cancel',
      hideCancelButton: false
    })
    return response === true
  }

  const handleNext = async () => {
    // Step 1 Choose which data type
    // Step 2 Upload file
    if (activeStep === 1) {
      // Check if report period is required but not selected
      if (selectedDataType === 'emissions' && !selectedReportPeriodId) {
        setShowReportPeriodError(true)
        return;
      }
      setShowReportPeriodError(false)
      //If no file uploaded, do nothing
      if (fileData.length === 0) {
        return;
      }
      //If custom spreadsheet, go to sheet configuration step
      if (isCustomSpreadsheet) {
        changeStep(2)
        return
      }
      //Bills go to their own multi-file endpoint; extraction then runs on the worker
      if (isBillUpload) {
        await handleUploadBills();
        return;
      }
      //If anything else, upload the file
      await handleUpload(activeStep);
      return;
    }
    // Step 3 when Custom spreadsheet and we don't yet have an upload -> perform upload now
    if (activeStep === 2 && isCustomSpreadsheet && !uploadResult) {
      // Upload with sheet/header and chosen mapping mode
      if (fileData.length === 0 || !selectedDataType) return
      if (blockOnIncompletePin()) return
      setIsUploading(true)
      setHasFetchedPendingErrors(false)
      setProcessingDone(false)
      setCommitRequested(false)
      // A fresh upload replaces whatever ?fileUploadId was left in the URL; keeping the old
      // id would let the restore fetch resurrect the wrong upload mid-flight.
      clearUrlFileUploadId()
      try {
        const form = new FormData()
        form.append('files', fileData[0])
        const customTypeOfData = getImportFileType(selectedDataType, emissionType, effectiveTemplateType)
        setTypeOfData(customTypeOfData)
        form.append('details', JSON.stringify(customTypeOfData))
        form.append('isCustomSpreadSheet', JSON.stringify(true))
        form.append('sheetNumber', JSON.stringify(customSheetNumber))
        form.append('headingRow', JSON.stringify(customHeaderRow))
        if (linkToExistingEmission) {
          form.append('linkToExistingEmission', 'true')
        }
        if (supplierIdFromUrl) {
          form.append('contactSupplierId', JSON.stringify(supplierIdFromUrl))
        }
        if (selectedDataType === 'suppliers') {
          form.append('importMode', importMode)
          form.append('importMatchKey', importMatchKey)
        }
        appendPinnedFactorYear(form)
        const response = await fileUploadServiceRef.current.uploadFile(form, selectedReportPeriodId ? `?reportPeriodId=${selectedReportPeriodId}` : null)
        setUploadResult(response)
        setCustomMappingComplete(false)
        if (response?.id) {
          // Same step, but record the upload's id as the refresh/bookmark aid.
          changeStep(2, { replace: true, fileUploadId: response.id })
        }
      } catch (error: any) {
        if (!error.response || error?.response?.status === 504) {
          navigate('/time-out?type=import')
        } else {
          userNotificationsContext.openDialog({
            active: true,
            title: 'Upload Failed',
            message: error?.response?.data?.message || 'There was an error, please try again',
            buttonCopy: 'Ok',
            hideCancelButton: true
          });
        }
      } finally {
        setIsUploading(false)
      }
      return
    }
    if (activeStep === 2 && isCustomSpreadsheet && uploadResult && customMappingComplete) {
      if (hasReconciliationStep) {
        changeStep(3)
        return
      }
      if (failedCount > 0 && !(await confirmPartialImport())) {
        return;
      }
      await handleImport();
      return;
    }
    //Step 3 when not Custom spreadsheet, import the records
    if (activeStep === 2 && !isCustomSpreadsheet) {
      if (hasReconciliationStep) {
        changeStep(3)
        return
      }
      if (failedCount > 0 && !(await confirmPartialImport())) {
        return;
      }
      await handleImport();
      return;
    }
    if (hasReconciliationStep && activeStep === 3) {
      if (failedCount > 0 && !(await confirmPartialImport())) {
        return;
      }
      await handleImport();
      return
    }
    if (activeStep < steps.length - 1) {
      changeStep(activeStep + 1)
    }
  }

  const handleBack = () => {
    if (activeStep > 0) {
      // Replace rather than push: the wizard's own Back mirrors the browser's, so pressing
      // browser Back afterwards must not bounce forward through the step just left.
      changeStep(activeStep - 1, { replace: true })
    }
  }

  const handleDataTypeSelect = (dataTypeId: string) => {
    setSelectedDataType(dataTypeId)
    setShowReportPeriodError(false)
    if (dataTypeId !== 'suppliers') {
      setImportMode('create')
      setImportMatchKey('name')
    }
    changeStep(1, { dataType: dataTypeId })
  }

  const handleStartNewImport = () => {
    setActiveStep(0)
    setSelectedDataType('')
    setFileData([])
    setUploadResult(null)
    setImportResult(null)
    setEmissionType('activity')
    setUploadMode('template')
    setTemplateType('rules')
    setLinkToExistingEmission(false)
    setCustomMappingComplete(false)
    setCanImportFromReconciliation(false)
    setHasFetchedPendingErrors(false)
    setFailedCount(0)
    setValidCount(0)
    setProcessingDone(false)
    setCommitDone(false)
    setCommitRequested(false)
    setRestoreError(null)
    setImportMode('create')
    setImportMatchKey('name')
    setSelectedConversionSourceId('')
    setSelectedConversionYear('')
    setPinFactorYear(false)
    // Return to a clean wizard URL: drops the finished import's ?fileUploadId (which would otherwise
    // re-fetch that upload) and any ?context=emissions, so "Import More" is a genuine fresh import.
    navigate('/settings/data-upload/import-data')
  }

  const handleRefreshValidation = async () => {
    if (!uploadResult || !selectedDataType) {
      return
    }
    try {
      const response = await fileUploadServiceRef.current.getUpload(uploadResult.id)
      if (JSON.stringify(response) !== JSON.stringify(uploadResult)) {
        setUploadResult(response)
      }
    } catch (error: any) {
      console.log(error)
    }
  }

  // Processing finished (or a bill run ended with per-document reasons to review). Re-fetch
  // the full upload the same way the old redirect's remount did, so review sees an identical
  // shape, then swap step 2's content to review in place.
  const handleProcessingComplete = async (progressUpload?: IFileUpload) => {
    try {
      const fresh = await fileUploadServiceRef.current.getUpload(activeUploadId)
      setUploadResult(fresh)
      if (fresh?.details) {
        setTypeOfData(fresh.details)
      }
      setLinkToExistingEmission(fresh?.linkToExistingEmission === true)
    } catch (error: any) {
      // The progress endpoint already handed us a usable upload; fall back to it.
      if (progressUpload) {
        setUploadResult(progressUpload)
        if (progressUpload.details) {
          setTypeOfData(progressUpload.details)
        }
      }
    }
    setProcessingDone(true)
  }

  // Restore-from-URL failed: the step content depends on an upload the API could not
  // return, so say so and offer a retry instead of spinning forever.
  const renderRestoreError = () => (
    <Box sx={{ py: 1 }}>
      <Alert severity="error" sx={{ mb: 3 }}>
        {restoreError}
      </Alert>
      <Button
        variant="contained"
        onClick={() => {
          setRestoreError(null)
          setRestoreAttempt(attempt => attempt + 1)
        }}
        sx={{ minWidth: 100 }}
      >
        <Typography>
          Try again
        </Typography>
      </Button>
    </Box>
  )

  // The processing run rendered as step 2's content: same polling and display as the
  // standalone progress page, but it advances the wizard in place when it completes.
  const renderProcessing = () => (
    <FileUploadProgress
      fileUploadId={activeUploadId}
      hideStepHeader
      onContinue={handleProcessingComplete}
    />
  )

  // The final step's content: the commit progress until the backend reports done, then the
  // success summary. On a refresh mid-commit this resumes from the URL's upload id alone
  // (the in-memory counts are gone, so the summary falls back to its generic line).
  const renderFinalStepContent = () => {
    if (!commitDone) {
      // Restoring from the URL: wait for the upload fetch before polling commit progress.
      // Without this, a refresh on the reconciliation step would briefly land here (the
      // reconciliation flag is not known yet) and poll a commit that was never requested.
      if (!uploadResult && fileUploadIdFromUrl) {
        if (restoreError) {
          return renderRestoreError()
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
            <LoadingSpinner />
          </Box>
        )
      }
      return (
        <FileCommitProgress
          fileUploadId={activeUploadId}
          hideStepHeader
          onComplete={() => setCommitDone(true)}
        />
      )
    }
    return (
      <ImportSuccessStep
        importResult={importResult}
        selectedDataType={selectedDataType || ''}
        onStartNewImport={handleStartNewImport}
        uploadResult={uploadResult}
      />
    )
  }

  const storeData = async (file: any) => {
    // A newly staged file supersedes any upload already in memory. Without this, going back
    // and picking a DIFFERENT file would leave the old uploadResult live, and the custom
    // path would skip sheet config and silently map/import the old file.
    if (uploadResult) {
      setUploadResult(null)
      setCustomMappingComplete(false)
      setProcessingDone(false)
      setHasFetchedPendingErrors(false)
      // Drop the superseded upload's id from the URL too, or the restore fetch would
      // immediately resurrect it.
      clearUrlFileUploadId()
    }
    // With `multiple`, react-drag-drop-files hands back a FileList rather than a File. Bills append
    // (a year of monthly bills is often dragged in a few batches); the spreadsheet path keeps its
    // single-file replace behaviour untouched.
    if (isBillUpload) {
      const incoming: any[] = file instanceof FileList ? Array.from(file) : [file]
      setFileData((existing: any[]) => [...existing, ...incoming])
      return
    }
    setFileData([file]);
  }

  const handleRemoveFile = (index: number) => {
    setFileData((existing: any[]) => existing.filter((_, i) => i !== index))
  }

  const handleUploadBills = async () => {
    if (fileData.length === 0 || !selectedReportPeriodId) {
      return
    }
    setIsUploading(true)
    setUploadResult(null)
    setHasFetchedPendingErrors(false)
    setProcessingDone(false)
    setCommitRequested(false)
    // A fresh upload replaces whatever ?fileUploadId was left in the URL; keeping the old
    // id would let the restore fetch resurrect the wrong upload mid-flight.
    clearUrlFileUploadId()
    try {
      const form = new FormData()
      fileData.forEach((file: any) => form.append('files', file))
      form.append('billType', billType)
      // Must mirror billTypeToImportType() on the backend. Energy bills name their own taxonomy, so
      // they use the manual template and need no org Rule; fuel bills still match a Rule because
      // their DEFRA taxonomy is too varied to name from a fuzzy "Diesel". Both resolve to the same
      // line model and the same commit path, and ValidationResultsStep renders either unchanged.
      setTypeOfData(billType === 'fuel' ? 'emissions-rules' : 'emissions-manual')
      const response = await fileUploadServiceRef.current.uploadBills(form, `?reportPeriodId=${selectedReportPeriodId}`)
      if (response?.id) {
        // Same in-place handoff as the spreadsheet path: step 2 shows the read running,
        // then swaps to review when it finishes. The wizard never unmounts. The endpoint
        // returns only an id, so hold a stub upload in memory (it keeps the restore
        // effect quiet); the full upload is fetched when processing completes.
        setUploadResult({ id: response.id, sourceType: 'BILL_DOCUMENTS' })
        changeStep(2, { fileUploadId: response.id })
      }
    } catch (error: any) {
      console.log(error)
      if (!error.response || error?.response?.status === 504) {
        navigate('/time-out?type=import')
        return
      }
      userNotificationsContext.openDialog({
        active: true,
        title: 'Upload Failed',
        message: error?.response?.data?.message || 'There was an error, please try again',
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleUpload = async (currentStep: number) => {
    if (fileData.length === 0 || !selectedDataType) {
      return;
    }
    if (selectedDataType === 'emissions' && (!emissionType || !selectedReportPeriodId)) {
      return;
    }
    if (blockOnIncompletePin()) return;
    const reportPeriodId = selectedDataType === 'emissions'
      ? selectedReportPeriodId
      : null;

    setIsUploading(true);
    setUploadResult(null);
    setHasFetchedPendingErrors(false);
    setProcessingDone(false);
    setCommitRequested(false);
    // A fresh upload replaces whatever ?fileUploadId was left in the URL; keeping the old
    // id would let the restore fetch resurrect the wrong upload mid-flight.
    clearUrlFileUploadId();
    try {
      const form = new FormData();
      form.append("files", fileData[0]);
      if (linkToExistingEmission) {
        form.append('linkToExistingEmission', 'true')
      }
      if (supplierIdFromUrl) {
        form.append('contactSupplierId', JSON.stringify(supplierIdFromUrl))
      }
      if (selectedDataType === 'suppliers') {
        form.append('importMode', importMode)
        form.append('importMatchKey', importMatchKey)
      }
      appendPinnedFactorYear(form)
      const calculatedTypeOfData = getImportFileType(selectedDataType, emissionType, effectiveTemplateType);
      setTypeOfData(calculatedTypeOfData);
      let queryParam = `?typeOfData=${calculatedTypeOfData}`;
      if (reportPeriodId) {
        queryParam += `&reportPeriodId=${reportPeriodId}`;
      }
      const response = await fileUploadServiceRef.current.uploadFile(form, queryParam);
      setUploadResult(response);
      if (response?.id) {
        // Stay mounted: step 2 shows the processing run and swaps to review in place
        // when it finishes, so nothing chosen on this step is lost in a remount.
        changeStep(currentStep + 1, { fileUploadId: response.id });
      } else {
        setProcessingDone(true);
        changeStep(currentStep + 1);
      }
    } catch (error: any) {
      console.log(error)
      if (!error.response || error?.response?.status === 504) {
        navigate('/time-out?type=import')
        return
      }
      // Show what the server actually said. A rejected upload has a specific cause (a closed report
      // period, an unreadable sheet) and hiding it behind a generic message leaves the user with
      // nothing to act on.
      userNotificationsContext.openSnackbar({ open: true, message: error?.response?.data?.message || 'There was an error, please try again', backgroundColor: 'text.primary' });
    } finally {
      setIsUploading(false);
    }
  }

  const handleImport = async () => {
    if (!uploadResult || !selectedDataType || commitRequested) {
      return;
    }
    setIsUploading(true);
    try {
      const importType = getImportFileType(selectedDataType, emissionType, effectiveTemplateType)
      const responseData = await fileUploadServiceRef.current.importFileUpload(uploadResult.id, {
        importType,
        emissionType: selectedDataType === 'emissions' ? emissionType : undefined
      });
      // The success step reports the counts the review step established, held in memory
      // (the wizard stays mounted). Only for the plain path: on a linked-activity import
      // not every valid row necessarily commits, so no counts are claimed.
      setImportResult(hasReconciliationStep
        ? responseData
        : { ...responseData, importedRowCount: validCount, skippedRowCount: failedCount })
      // Advance to the final step in place. Async commit types show the commit progress
      // there until the backend reports done; sync types were committed inside the request
      // just made (lineCommitStatus never moves for them), so the summary shows at once.
      setCommitRequested(true);
      setCommitDone(!isAsyncCommitType(importType));
      setIsUploading(false);
      changeStep(successStepIndex);
    } catch (error: any) {
      setIsUploading(false);
      if (!error.response || error?.response?.status === 504) {
        navigate('/time-out?type=import')
      } else {
        userNotificationsContext.openDialog({
          active: true,
          title: 'Import Failed',
          message: error?.response?.data?.message || 'There was an error, please try again',
          buttonCopy: 'Ok',
          hideCancelButton: true
        });
      }
    }
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        //Choose which data type
        return (
          <DataTypeSelectionStep onDataTypeSelect={handleDataTypeSelect} />
        )
      case 1:
        //Upload file for data type
        if (!selectedDataType) {
          return;
        }
        return (
          <UploadMethodStep
            selectedDataType={selectedDataType}
            uploadMode={uploadMode}
            emissionType={emissionType}
            selectedReportPeriodId={selectedReportPeriodId}
            onUploadModeSelect={handleUploadModeSelect}
            onEmissionTypeChange={handleEmissionTypeChange}
            onReportPeriodChange={handleReportPeriodChange}
            onFileUpload={storeData}
            templateType={templateType}
            onTemplateTypeChange={handleTemplateTypeChange}
            isCustomAllowed={isCustomAllowed}
            showReportPeriodError={showReportPeriodError}
            linkToExistingEmission={linkToExistingEmission}
            onLinkToExistingEmissionChange={setLinkToExistingEmission}
            importMode={importMode}
            importMatchKey={importMatchKey}
            onImportModeChange={setImportMode}
            onImportMatchKeyChange={setImportMatchKey}
            isBillAllowed={isBillAllowed}
            billType={billType}
            onBillTypeChange={setBillType}
            stagedFiles={fileData}
            onRemoveFile={handleRemoveFile}
            selectedConversionSourceId={selectedConversionSourceId}
            selectedConversionYear={selectedConversionYear}
            onConversionSourceChange={setSelectedConversionSourceId}
            onConversionYearChange={setSelectedConversionYear}
            pinFactorYear={pinFactorYear}
            onPinFactorYearChange={setPinFactorYear}
          />
        )
      case 2:
        // Restoring from the URL and the upload could not be fetched: everything below
        // needs it, so show the reason and a retry instead of an endless spinner.
        if (!uploadResult && fileUploadIdFromUrl && restoreError) {
          return renderRestoreError()
        }
        if (isCustomSpreadsheet) {
          //If Custom Spreadsheet and file hasn't been uploaded yet, show sheet configuration
          if (!uploadResult) {
            return (
              <SheetConfigurationStep
                fileData={fileData}
                onSheetNumberChange={(n) => setCustomSheetNumber(n)}
                onHeaderRowChange={(r) => setCustomHeaderRow(r)}
              />
            )
          }
          //Mapping submitted: the backend is re-reading the file with it. Watch that run here.
          if (customMappingComplete && !processingDone) {
            return renderProcessing()
          }
          //If Custom Spreadsheet and file has been uploaded and mapping is complete, show validation results
          if (customMappingComplete && typeOfData) {
            return <ValidationResultsStep uploadResult={uploadResult} typeOfData={typeOfData} onRefreshValidation={handleRefreshValidation} onCanImportChange={(v) => setCanImport(v)} onHasFetchedPendingErrorsChange={(v) => setHasFetchedPendingErrors(v)} onFailedCountChange={(v) => setFailedCount(v)} onValidCountChange={(v) => setValidCount(v)} />
          }
          //If Custom Spreadsheet and file has been uploaded and mapping is not complete, show custom mapping
          return (
            <CustomMappingStep
              uploadId={uploadResult.id}
              onComplete={() => {
                // Mapping saved; the backend now reads the file with it. Stay mounted,
                // watch that run as this step's content, then fall through to review.
                setCustomMappingComplete(true)
                setProcessingDone(false)
              }}
              onBack={() => {
                setUploadResult(null)
                setCustomMappingComplete(false)
                // Drop the abandoned upload's id or the restore fetch would bring it back.
                clearUrlFileUploadId()
              }}
            />
          )
        }
        //Processing still running (fresh upload, or restored from a refresh): watch it here.
        if (!processingDone && activeUploadId) {
          return renderProcessing()
        }
        if (!selectedDataType || !typeOfData) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
              <LoadingSpinner />
            </Box>
          )
        }
        //If not Custom Spreadsheet, show validation results
        return <ValidationResultsStep uploadResult={uploadResult} typeOfData={typeOfData} onRefreshValidation={handleRefreshValidation} onCanImportChange={(v) => setCanImport(v)} onHasFetchedPendingErrorsChange={(v) => setHasFetchedPendingErrors(v)} onFailedCountChange={(v) => setFailedCount(v)} onValidCountChange={(v) => setValidCount(v)} />
      case 3:
        if (hasReconciliationStep) {
          if (!typeOfData || !uploadResult) {
            return null
          }
          return (
            <LinkReconciliationStep
              uploadResult={uploadResult}
              typeOfData={typeOfData}
              onHasReadyRowsChange={setCanImportFromReconciliation}
            />
          )
        }
        return renderFinalStepContent()
      case 4:
        return renderFinalStepContent()
      default:
        return null
    }
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 5 }}>
      <Box sx={{ width: '100%', maxWidth: 880 }}>
        <ImportStepHeader currentStep={activeStep + 1} totalSteps={steps.length} />
        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
        </Box>
        {activeStep > 0 && activeStep < successStepIndex && !(isCustomSpreadsheet && activeStep === 2 && !!uploadResult && !customMappingComplete) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ChevronLeft />}
              sx={{ minWidth: 100 }}>
              <Typography>
                Back
              </Typography>
            </Button>
            {/* While step 2 is showing the processing run there is nothing to advance to by
                hand (the step swaps to review on its own), so only Back is offered. */}
            {!isAwaitingProcessing && <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                isUploading
                // A commit was already requested for this upload: never offer a second
                // one, however the user navigated back here.
                || commitRequested
                || (activeStep === 1 && fileData.length === 0)
                || (activeStep === 2 && !hasFetchedPendingErrors && !(isCustomSpreadsheet && !uploadResult))
                || (activeStep === 2 && (!isCustomSpreadsheet ? !canImport : (!!uploadResult && customMappingComplete ? !canImport : false)))
                || (hasReconciliationStep && activeStep === 3 && !canImportFromReconciliation)
              }
              endIcon={<ChevronRight />}
              sx={{ minWidth: 100 }}
            >
              <Typography>
                {isUploading ? 'Processing...' :
                  activeStep === 1 && fileData.length > 0 ? (isCustomSpreadsheet ? 'Continue' : isBillUpload ? `Read ${fileData.length} ${fileData.length === 1 ? 'bill' : 'bills'}` : 'Upload data') :
                    activeStep === 2 ? (
                      isCustomSpreadsheet
                        ? (!uploadResult ? 'Continue' : (customMappingComplete ? (hasReconciliationStep ? 'Match links' : 'Import data') : 'Next'))
                        : (hasReconciliationStep ? 'Match links' : (isBillReview && validCount > 0 ? `Import ${validCount} ready ${validCount === 1 ? 'row' : 'rows'}` : 'Import data'))
                    ) : activeStep === 3 && hasReconciliationStep ? 'Import data' : 'Next'}
              </Typography>
            </Button>}
          </Box>
        )}
      </Box>
    </Box>
  )
}